import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import AudioUrlService from '../services/AudioUrlService';

const AudioContext = createContext();

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  // Audio state
  const [currentAudio, setCurrentAudio] = useState(null); // { id, title, audioUrl, format, durationSeconds }
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // Current time in seconds
  const [duration, setDuration] = useState(0); // Total duration in seconds
  const [volume, setVolumeState] = useState(() => {
    const saved = localStorage.getItem('audioPlayerVolume');
    return saved ? parseFloat(saved) : 0.8;
  });
  const [playbackRate, setPlaybackRateState] = useState(() => {
    const saved = localStorage.getItem('audioPlayerPlaybackRate');
    return saved ? parseFloat(saved) : 1.0;
  });
  const [isMinimized, setIsMinimized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generation state — persists across route navigation since AudioProvider is at app root
  // { id, toolType, title, stage, error, status: 'generating'|'complete'|'failed' } | null
  const [generationJob, setGenerationJob] = useState(null);

  // Audio element ref
  const audioRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = 'metadata';
      
      // Set initial volume and playback rate
      audioRef.current.volume = volume;
      audioRef.current.playbackRate = playbackRate;

      // Event listeners
      audioRef.current.addEventListener('loadedmetadata', () => {
        setDuration(audioRef.current.duration);
        setIsLoading(false);
      });

      audioRef.current.addEventListener('ended', () => {
        setIsPlaying(false);
        setProgress(0);
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
      });

      audioRef.current.addEventListener('error', async (e) => {
        // Ignore errors when no source is set (browser fires spurious error on empty Audio())
        if (!audioRef.current?.src || audioRef.current.src === '' || audioRef.current.src === window.location.href) {
          return;
        }
        console.error('Audio playback error:', e);
        
        // Try to refresh the URL if it's an access error (use documentId for refresh)
        if (currentAudio?.documentId) {
          try {
            const newUrl = await AudioUrlService.getOrRefreshUrl(currentAudio.documentId, true);
            if (newUrl && audioRef.current) {
              const currentTime = audioRef.current.currentTime;
              audioRef.current.src = newUrl;
              audioRef.current.currentTime = currentTime;
              audioRef.current.play();
              setError(null);
              return;
            }
          } catch (refreshError) {
            console.error('Failed to refresh audio URL:', refreshError);
          }
        }
        
        setError('Failed to load audio. Please try again.');
        setIsLoading(false);
        setIsPlaying(false);
      });

      audioRef.current.addEventListener('play', () => {
        setIsPlaying(true);
      });

      audioRef.current.addEventListener('pause', () => {
        setIsPlaying(false);
      });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Warn user before closing/navigating away while audio is playing
  useEffect(() => {
    if (!isPlaying) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isPlaying]);

  // Update progress interval when playing state changes
  useEffect(() => {
    if (isPlaying && audioRef.current) {
      progressIntervalRef.current = setInterval(() => {
        if (audioRef.current) {
          setProgress(audioRef.current.currentTime);
        }
      }, 100);
    } else {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [isPlaying]);

  // Load and play audio
  // audioData: { id, title, audioUrl (already-resolved stream URL), format, durationSeconds, documentId? }
  const playAudio = useCallback(async (audioData) => {
    if (!audioData?.audioUrl) {
      setError('Invalid audio data');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // audioUrl is already a resolved signed URL from the caller
      const streamUrl = audioData.audioUrl;

      if (audioRef.current) {
        // If switching to a new audio file
        if (currentAudio?.id !== audioData.id) {
          audioRef.current.pause();
          audioRef.current.src = streamUrl;
          audioRef.current.load();
          setCurrentAudio(audioData);
          setProgress(0);
          setDuration(audioData.durationSeconds || 0);
        }

        // Update volume and playback rate
        audioRef.current.volume = volume;
        audioRef.current.playbackRate = playbackRate;

        // Play
        await audioRef.current.play();
        setIsPlaying(true);
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error playing audio:', err);
      setError('Failed to play audio. Please try again.');
      setIsLoading(false);
      setIsPlaying(false);
    }
  }, [currentAudio, volume, playbackRate]);

  // Pause audio
  const pauseAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pauseAudio();
    } else if (currentAudio) {
      playAudio(currentAudio);
    }
  }, [isPlaying, currentAudio, pauseAudio, playAudio]);

  // Seek to position
  const seekTo = useCallback((seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(seconds, duration));
      setProgress(audioRef.current.currentTime);
    }
  }, [duration]);

  // Set volume
  const setVolume = useCallback((newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);
    localStorage.setItem('audioPlayerVolume', clampedVolume.toString());
    
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
  }, []);

  // Set playback rate
  const setPlaybackRate = useCallback((rate) => {
    const clampedRate = Math.max(0.25, Math.min(2.0, rate));
    setPlaybackRateState(clampedRate);
    localStorage.setItem('audioPlayerPlaybackRate', clampedRate.toString());
    
    if (audioRef.current) {
      audioRef.current.playbackRate = clampedRate;
    }
  }, []);

  // Clear audio (close player)
  const clearAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setCurrentAudio(null);
    setIsPlaying(false);
    setProgress(0);
    setDuration(0);
    setError(null);
    setIsLoading(false);
    setIsMinimized(false);
  }, []);

  // Toggle minimize
  const toggleMinimize = useCallback(() => {
    setIsMinimized(prev => !prev);
  }, []);

  // ─── Generation lifecycle ──────────────────────────────────────────

  // Start tracking a new audio generation job (podcast or audiobook).
  // Returns false if a generation is already in progress.
  const startGeneration = useCallback(({ id, toolType, title: genTitle }) => {
    if (generationJob && generationJob.status === 'generating') {
      console.warn('[AudioContext] Generation already in progress — ignoring startGeneration');
      return false;
    }
    setGenerationJob({ id, toolType, title: genTitle, stage: null, error: null, status: 'generating' });
    return true;
  }, [generationJob]);

  // Update the SSE stage text shown in the global player during generation
  const updateGenerationStage = useCallback((stage) => {
    setGenerationJob(prev => prev ? { ...prev, stage } : prev);
  }, []);

  // Mark generation complete. The caller should auto-play the result separately via playAudio().
  const completeGeneration = useCallback(() => {
    setGenerationJob(prev => prev ? { ...prev, status: 'complete', stage: null } : null);
    // Auto-clear the generation banner after a short delay so users see "Complete" briefly
    setTimeout(() => setGenerationJob(null), 2000);
  }, []);

  // Mark generation as failed. The global player shows the error with a dismiss button.
  const failGeneration = useCallback((errorMsg) => {
    setGenerationJob(prev => prev ? { ...prev, status: 'failed', error: errorMsg, stage: null } : null);
  }, []);

  // Dismiss generation state (user closes the error banner or it auto-clears)
  const clearGeneration = useCallback(() => {
    setGenerationJob(null);
  }, []);

  // Warn before tab close during generation
  useEffect(() => {
    if (!generationJob || generationJob.status !== 'generating') return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [generationJob]);

  const value = {
    // State
    currentAudio,
    isPlaying,
    progress,
    duration,
    volume,
    playbackRate,
    isMinimized,
    isLoading,
    error,
    
    // Actions
    playAudio,
    pauseAudio,
    togglePlayPause,
    seekTo,
    setVolume,
    setPlaybackRate,
    clearAudio,
    toggleMinimize,

    // Generation state + actions
    generationJob,
    startGeneration,
    updateGenerationStage,
    completeGeneration,
    failGeneration,
    clearGeneration,
  };

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
};
